import { createContext, useContext, useRef, useState } from "react";
import { List } from "@corpuscomplete/core/common/list";
import { NotFoundPage } from "@corpuscomplete/web/components/not-found-page";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import { PageHeader } from "@corpuscomplete/web/components/layout";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Checkbox } from "@corpuscomplete/web-ui/components/checkbox";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { Ellipsis, GripVertical } from "lucide-react";
import { useSyncedInput } from "@corpuscomplete/web/hooks/use-synced-input";
import { type StoreApi, createStore, useStore as useZustandStore } from "zustand";
import { useClickOutside } from "@corpuscomplete/web/hooks/use-click-outside";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { restrictToFirstScrollableAncestor } from "@dnd-kit/modifiers";
import { must, sortIndexBetween } from "@corpuscomplete/core/utils";
import { ListProgress } from "./list-progress";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { Zero } from "@rocicorp/zero";
import { Input } from "@corpuscomplete/web-ui/components/input";

export namespace ListComponent {
  type DataContext = {
    list: List.Info;
  };
  const DataContext = createContext<DataContext | null>(null);
  export const DataProvider = ({
    children,
    listId,
  }: {
    children: React.ReactNode;
    listId: string;
  }) => {
    const [listRaw, listDetails] = useQuery(queries.list.details({ id: listId }));
    if (listDetails.type === "unknown") {
      return null;
    }
    if (!listRaw) {
      // TODO: Probably need this to be a component passed in
      return <NotFoundPage />;
    }

    const list = List.asInfo(listRaw);
    return <DataContext.Provider value={{ list }}>{children}</DataContext.Provider>;
  };
  const useData = () => {
    const data = useContext(DataContext);
    if (!data) {
      throw new Error("Missing DataProvider");
    }
    return data;
  };

  type DnDData = { id: string; type: "section" } | { id: string; type: "item" };

  type DropTarget = {
    prev: DnDData | null;
    next: DnDData | null;
  };

  type Store = {
    selectedId: string | null;
    setSelectedId: (id: string | null) => void;
    dragging: DnDData | null;
    setDragging: (dragging: DnDData | null) => void;
  };
  const StoreContext = createContext<StoreApi<Store> | null>(null);
  export const StoreProvider = ({ children }: { children: React.ReactNode }) => {
    const [store] = useState(() =>
      createStore<Store>((set) => ({
        selectedId: null,
        setSelectedId: (selectedId) => set({ selectedId }),
        dragging: null,
        setDragging: (dragging) => set({ dragging }),
      })),
    );
    return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
  };
  const useStore = <T extends any>(selector: (state: Store) => T) => {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error("Missing StoreProvider");
    }
    return useZustandStore(store, selector);
  };

  export function Header() {
    const { list } = useData();
    const z = useZero();
    const titleInput = useSyncedInput({
      syncedValue: list.title,
      save: (title) => {
        z.mutate(mutators.list.update({ id: list.id, title }));
      },
    });
    return (
      <PageHeader>
        <ListProgress list={list} size="sm" />
        <Input
          variant="ghost"
          textVariant="sm"
          className="font-medium"
          value={titleInput.value}
          onChange={titleInput.onChange}
          onFocus={titleInput.onFocus}
          onBlur={titleInput.onBlur}
          onKeyDown={titleInput.onKeyDown}
        />
      </PageHeader>
    );
  }

  export function Content() {
    const { list } = useData();
    const z = useZero();
    const dragging = useStore((state) => state.dragging);
    const setDragging = useStore((state) => state.setDragging);
    const setSelectedId = useStore((state) => state.setSelectedId);

    let prevData: DnDData | null = null;
    return (
      <DndContext
        modifiers={[restrictToFirstScrollableAncestor]}
        onDragStart={(e) => {
          const data = e.active.data.current as DnDData;
          setDragging(data);
          setSelectedId(null);
        }}
        onDragEnd={(e) => {
          const dragging = e.active.data.current as DnDData | undefined;
          const dropTarget = e.over?.data.current as DropTarget | undefined;
          if (dragging && dropTarget && canDrop(dragging, dropTarget)) {
            handleDrop(list, dragging, dropTarget, z);
          }
          setDragging(null);
        }}
      >
        {list.orphanItems.map((item) => {
          const prevDataForItem = prevData;
          prevData = { id: item.id, type: "item" };
          return <ListItemView key={item.id} item={item} prevData={prevDataForItem} />;
        })}
        {list.sections.map((section) => {
          const prevDataForSection = prevData;
          prevData = { id: section.id, type: "section" };
          return (
            <div key={section.id}>
              <ListSectionView section={section} prevData={prevDataForSection} />
              {section.items.map((item) => {
                const prevDataForItem = prevData;
                prevData = { id: item.id, type: "item" };
                return <ListItemView key={item.id} item={item} prevData={prevDataForItem} />;
              })}
            </div>
          );
        })}
        <div className="relative">
          <ListRowDropTarget prev={prevData} next={null} />
        </div>
        <DragOverlay dropAnimation={null}>
          {dragging?.type === "item" && <ListItemForDragOverlay id={dragging.id} />}
          {dragging?.type === "section" && <ListSectionForDragOverlay id={dragging.id} />}
        </DragOverlay>
      </DndContext>
    );
  }

  function canDrop(dragging: DnDData, { next }: DropTarget) {
    if (dragging.type === "item") {
      return true;
    } else if (dragging.type === "section") {
      return next === null || next.type === "section";
    }
    return false;
  }

  // Some repeated logic here, but doesn't seem worth refactoring for now
  function handleDrop(list: List.Info, dragging: DnDData, { prev, next }: DropTarget, z: Zero) {
    if (dragging.type === "item") {
      if (prev === null || prev?.type === "section") {
        const section = list.sections.find((section) => section.id === prev?.id);
        const items = section?.items ?? list.orphanItems;
        const firstItem = items[0];
        if (firstItem?.id === dragging.id) {
          return;
        }
        const sortIndex = sortIndexBetween(null, firstItem?.sortIndex);
        return z.mutate(
          mutators.list.updateItem({
            id: dragging.id,
            listId: list.id,
            sortIndex,
            sectionId: section?.id ?? null,
          }),
        );
      } else if (prev?.type === "item" && next?.type === "item") {
        const section = list.sections.find((section) =>
          section.items.some((item) => item.id === prev.id),
        );
        const items = section?.items ?? list.orphanItems;
        const prevItem = items.find((item) => item.id === prev.id);
        const nextItem = items.find((item) => item.id === next.id);
        if (prevItem?.id === dragging.id || nextItem?.id === dragging.id) {
          return;
        }
        const sortIndex = sortIndexBetween(prevItem?.sortIndex, nextItem?.sortIndex);
        return z.mutate(
          mutators.list.updateItem({
            id: dragging.id,
            listId: list.id,
            sortIndex,
            sectionId: section?.id ?? null,
          }),
        );
      } else if (prev?.type === "item" && (next === null || next?.type === "section")) {
        const section = list.sections.find((section) =>
          section.items.some((item) => item.id === prev.id),
        );
        const items = section?.items ?? list.orphanItems;
        const lastItem = items[items.length - 1];
        if (lastItem?.id === dragging.id) {
          return;
        }
        const sortIndex = sortIndexBetween(lastItem?.sortIndex, null);
        return z.mutate(
          mutators.list.updateItem({
            id: dragging.id,
            listId: list.id,
            sortIndex,
            sectionId: section?.id ?? null,
          }),
        );
      }
    } else if (dragging.type === "section") {
      if (next === null) {
        const lastSection = list.sections[list.sections.length - 1];
        if (lastSection?.id === dragging.id) {
          return;
        }
        const sortIndex = sortIndexBetween(lastSection?.sortIndex, null);
        return z.mutate(
          mutators.list.updateSection({
            id: dragging.id,
            listId: list.id,
            sortIndex,
          }),
        );
      } else if (next?.type === "section") {
        const sectionIndex = list.sections.findIndex((section) => section.id === next.id);
        const section = must(list.sections[sectionIndex]);
        const prevSection = list.sections[sectionIndex - 1];
        if (dragging.id === section.id || dragging.id === prevSection?.id) {
          return;
        }
        const sortIndex = sortIndexBetween(prevSection?.sortIndex, section.sortIndex);
        return z.mutate(
          mutators.list.updateSection({
            id: dragging.id,
            listId: list.id,
            sortIndex,
          }),
        );
      }
    }
    console.warn("Handle drop not handled", { dragging, prev, next });
  }

  function ListSectionForDragOverlay({ id }: { id: string }) {
    const { list } = useData();
    const section = list.sections.find((section) => section.id === id);
    return (
      <div className="inline rounded-md bg-gray-50 p-2 shadow">
        <Text variant="sm">{section?.title ?? "Unknown"}</Text>
      </div>
    );
  }

  function ListItemForDragOverlay({ id }: { id: string }) {
    // Would like this to be a local-only query since the data should be on the client based on the List.details query
    // But zero was throwing an error about the query not being supported
    //
    // Or maybe just find the item in "list" from useData()?
    const z = useZero();
    const [rawItem] = useQuery(List.itemDetails(z, id), {
      enabled: !!id,
    });
    const item = rawItem && List.asItem(rawItem);
    return (
      <div className="inline rounded-md bg-gray-50 p-2 shadow">
        <Text variant="sm">{item?.label ?? "Unknown"}</Text>
      </div>
    );
  }

  function ListRow({
    prevData,
    data,
    children,
    className,
  }: {
    prevData: DnDData | null;
    data: DnDData;
    children: React.ReactNode;
    className?: string;
  }) {
    const {
      attributes,
      listeners,
      setNodeRef: setDragRef,
      transform,
    } = useDraggable({
      id: data.id,
      data,
    });
    const style = {
      transform: CSS.Translate.toString(transform),
    };

    return (
      <div className={cn("group relative z-10 pl-6", className)} ref={setDragRef}>
        <GripVertical
          className="absolute top-1/2 left-0 mt-[-10px] text-gray-500 opacity-0 transition-opacity group-hover:opacity-100"
          style={style}
          {...attributes}
          {...listeners}
          size={20}
        />
        {children}
        <ListRowDropTarget prev={prevData} next={data} />
      </div>
    );
  }

  function ListRowDropTarget({
    prev,
    next,
    className,
  }: {
    prev: DnDData | null;
    next: DnDData | null;
    className?: string;
  }) {
    const dropTarget: DropTarget = { prev, next };
    const {
      setNodeRef: setDropRef,
      isOver,
      active,
    } = useDroppable({
      id: `${prev?.id}-${next?.id}`,
      data: dropTarget,
    });
    const displayDropTarget = active && canDrop(active.data.current as DnDData, dropTarget);
    return (
      <div ref={setDropRef} className={cn("absolute top-0 right-0 left-0 pl-6", className)}>
        {displayDropTarget && <div className={cn("h-[2px] w-full", isOver && "bg-blue-500")} />}
      </div>
    );
  }

  function ListSectionView({
    section,
    prevData,
  }: {
    section: List.Section;
    prevData: DnDData | null;
  }) {
    const z = useZero();

    const isSelected = useStore((state) => state.selectedId === section.id);
    const setSelectedId = useStore((state) => state.setSelectedId);
    const ref = useRef<HTMLDivElement | null>(null);
    useClickOutside(ref, () => setSelectedId(null), {
      enabled: isSelected,
    });

    const titleInput = useSyncedInput({
      syncedValue: section.title,
      save: (title) => {
        z.mutate(
          mutators.list.updateSection({
            id: section.id,
            listId: section.listId,
            title,
          }),
        );
      },
      onFocus: () => setSelectedId(section.id),
    });

    return (
      <div ref={ref} className={cn(isSelected && "bg-gray-100")}>
        <ListRow prevData={prevData} data={{ id: section.id, type: "section" }}>
          <div className="flex flex-row items-center gap-2">
            <Input
              variant="ghost"
              textVariant="h4"
              value={titleInput.value}
              onChange={titleInput.onChange}
              onFocus={titleInput.onFocus}
              onBlur={titleInput.onBlur}
              onKeyDown={titleInput.onKeyDown}
            />
            <Button size="icon-sm" variant="ghost">
              <Ellipsis className="size-4" />
            </Button>
          </div>
        </ListRow>
      </div>
    );
  }

  function ListItemView({ item, prevData }: { item: List.Item; prevData: DnDData | null }) {
    const z = useZero();

    const isSelected = useStore((state) => state.selectedId === item.id);
    const setSelectedId = useStore((state) => state.setSelectedId);
    const ref = useRef<HTMLDivElement | null>(null);
    useClickOutside(ref, () => setSelectedId(null), {
      enabled: isSelected,
    });

    const labelInput = useSyncedInput({
      syncedValue: item.label,
      save: (label) => {
        z.mutate(
          mutators.list.updateItem({
            id: item.id,
            listId: item.listId,
            label,
          }),
        );
      },
      onFocus: () => setSelectedId(item.id),
    });
    const noteInput = useSyncedInput({
      mode: "textarea",
      syncedValue: item.note ?? "",
      save: (note) => {
        z.mutate(
          mutators.list.updateItem({
            id: item.id,
            listId: item.listId,
            note: note || null,
          }),
        );
      },
      onFocus: () => setSelectedId(item.id),
    });

    return (
      <div ref={ref} className={cn(isSelected && "bg-gray-100")}>
        <ListRow prevData={prevData} data={{ id: item.id, type: "item" }}>
          <div className="flex flex-row items-center gap-2">
            <Checkbox
              checked={item.status !== "pending"}
              onCheckedChange={(checked) =>
                z.mutate(
                  mutators.list.updateItemStatus({
                    id: item.id,
                    listId: item.listId,
                    status: checked ? "complete" : "pending",
                  }),
                )
              }
            />
            <Input
              variant="ghost"
              className={cn(item.status !== "pending" && "line-through")}
              value={labelInput.value}
              onChange={labelInput.onChange}
              onFocus={labelInput.onFocus}
              onBlur={labelInput.onBlur}
              onKeyDown={labelInput.onKeyDown}
            />
          </div>
        </ListRow>
        <div
          className={cn(
            "flex flex-row items-center pl-12",
            "overflow-hidden transition-[max-height] ease-in-out",
            isSelected ? "max-h-96 duration-200" : "max-h-0 duration-100",
          )}
        >
          <textarea
            className={cn(
              "min-h-14 w-auto min-w-0 flex-1 border-0 bg-transparent px-0 py-0 py-1 text-sm leading-none shadow-none focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
            )}
            placeholder="Add a note..."
            value={noteInput.value}
            onChange={noteInput.onChange}
            onFocus={noteInput.onFocus}
            onBlur={noteInput.onBlur}
            onKeyDown={noteInput.onKeyDown}
          />
        </div>
      </div>
    );
  }
}
